import { isEqual } from 'lodash';
import { navigateTo } from '@cat-ecom/common/utils/browserUtils';
import { selectEquipment } from 'services/equipment/actions';
import { getSeoUrl } from 'utils/links';
import {
  ALP_URL,
  MAINTENANCE_REPAIRS_PATH,
  PARTS_DIAGRAM_PATH,
  HEADLESS_ALP_PAGE
} from 'constants/links';
import { Params } from 'next/dist/shared/lib/router/utils/route-matcher';
import { capitalizeText } from '@cat-ecom/common/utils/commonUtils';

export const redirectFromEquipment = async (
  url: string,
  isSelectedEquipment: boolean,
  equipmentDetails: {
    id: string;
    serialNumber: string;
  }
) => {
  const response =
    !isSelectedEquipment &&
    (await selectEquipment(
      equipmentDetails?.id,
      equipmentDetails?.serialNumber,
      false
    ));

  if (isSelectedEquipment || response.ok) {
    navigateTo(url);
  }
};

export const isExistingSuggestion = (data, suggestions) => {
  return (
    isEqual(
      suggestions?.equipments,
      data?.equipments || data?.matchedEquipment || data?.equipment
    ) &&
    isEqual(
      suggestions?.otherEquipments,
      data?.otherEquipments || data?.partiallyMatchedEquipment
    )
  );
};

export const checkIsAlpPage = (params, path: string) => {
  const { ['part-number']: partNumber, ['part-name']: partName } = params;
  const esitURL = getSeoUrl(params, HEADLESS_ALP_PAGE);
  const alpURL = `${esitURL}/${partName}/${partNumber}`;
  const alp = path === alpURL;
  const subpage = path.includes(alpURL);
  return { isAlp: alp, isAlpSubpage: subpage };
};

export const handleSuggestionResults = (data) => {
  let suggestions = null;
  if ('equipments' in data) {
    suggestions = {
      equipments: data.equipments ?? [],
      otherEquipments: data.otherEquipments ?? []
    };
  } else if ('matchedEquipment' in data) {
    suggestions = {
      equipments: data.matchedEquipment ?? [],
      otherEquipments: data.partiallyMatchedEquipment ?? []
    };
  } else if ('equipment' in data) {
    suggestions = {
      equipments: data.equipment ?? []
    };
  }
  return suggestions;
};

export const getFormattedSeoUrl = (seoUrl: string, params: Params) => {
  const { esite, lang } = params;
  return seoUrl.replace(`/${lang}/${esite}`, '');
};

export const redirectToNewAlpUrl = async (
  router,
  featureFlag: boolean,
  params: Params,
  seoUrl: string,
  isSelectedEquipment = true,
  equipmentDetails?: {
    id: string;
    serialNumber: string;
  }
) => {
  const response =
    !isSelectedEquipment &&
    (await selectEquipment(
      equipmentDetails?.id,
      equipmentDetails?.serialNumber,
      false
    ));
  const formattedSeoUrl = getFormattedSeoUrl(seoUrl, params);
  const alpUrl = featureFlag ? `${ALP_URL}${formattedSeoUrl}` : formattedSeoUrl;
  const url = getSeoUrl(params, alpUrl);
  if (isSelectedEquipment || response.ok) {
    if (featureFlag) {
      router.push(url);
    } else {
      navigateTo(url);
    }
  } else {
    router.push(url);
  }
};

export const getAlpSeoUrl = (seoUrl, pageUrl, params) => {
  const formattedUrl = getFormattedSeoUrl(seoUrl, params);
  const pathMap = {
    [PARTS_DIAGRAM_PATH]: pageUrl.includes(PARTS_DIAGRAM_PATH),
    [MAINTENANCE_REPAIRS_PATH]: pageUrl.includes(MAINTENANCE_REPAIRS_PATH)
  };
  const matchingPath = Object.keys(pathMap).find((key) => pathMap[key]) || '';
  return getSeoUrl(params, `${ALP_URL}${formattedUrl}${matchingPath}`);
};

export const getCategoryOptions = (repairOptions, defaultCategory = []) => {
  if (!repairOptions.length) return [];
  const uniqueCategories = new Set();
  return [
    ...defaultCategory,
    ...repairOptions
      .map(({ category }) => {
        const categoryName = capitalizeText(category);
        return uniqueCategories.has(categoryName)
          ? null
          : (uniqueCategories.add(categoryName),
            { label: categoryName, value: categoryName });
      })
      .filter(Boolean)
  ];
};
